/* eslint-disable no-debugger */
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack5';
import { Form, FormikProvider, useFormik } from 'formik';
// material
import { LoadingButton } from '@material-ui/lab';
import {
  Box,
  Card,
  Grid,
  Stack,
  TextField,
  Typography,
  FormHelperText,
  Autocomplete,
  Button,
  styled,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  Checkbox,
  FormControlLabel
} from '@material-ui/core';
// utils
import { useTheme } from '@material-ui/styles';
// eslint-disable-next-line import/no-unresolved
import { Icon } from '@iconify/react';
import closeFill from '@iconify/icons-eva/close-fill';
import fileFill from '@iconify/icons-eva/file-fill';
// eslint-disable-next-line import/no-unresolved
import * as XLSX from 'xlsx';
// eslint-disable-next-line import/no-unresolved
import { varFadeInRight } from 'src/components/animate';
import { AnimatePresence, motion } from 'framer-motion';
/* eslint-disable import/no-unresolved */

import { getProjectListByClient, resetProjectListByClient } from 'src/redux/slices/client';
import { getCategoryListByProject, resetCategoryListByProject } from 'src/redux/slices/filecategory';
import { MIconButton } from '../../@material-extend';
import { UploadSingleFile } from '../../upload';
import FilePreview from '../../common-components/FilePreview';
import { useDispatch, useSelector } from '../../../redux/store';
import { fData } from '../../../utils/formatNumber';
import {
  getColumnNames,
  createColumnmap,
  resetColumnError,
  resetCreateColumnStatus,
  getCopyExternalColumns
} from '../../../redux/slices/columnmap';

// ----------------------------------------------------------------------
const LabelStyle = styled(Typography)(({ theme }) => ({
  ...theme.typography.subtitle2,
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1)
}));

const previewColumnFile = ({ Data }) => (
  <TableContainer sx={{ minWidth: 800, overflow: 'scroll', height: 450 }}>
    <Table stickyHeader sx={{ border: 1 }}>
      <TableBody>
        {Data.map((Dataa, index) => (
          <TableRow sx={{ bgcolor: index % 2 === 0 ? '#3cb37117' : 'white', border: 1 }} key={index}>
            {Dataa.map((temp, tempIndex) => (
              <TableCell sx={{ border: 1 }} key={tempIndex}>
                {temp}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

ColumnMapView.propTypes = {
  id: PropTypes.number,
  setId: PropTypes.func,
  currentData: PropTypes.object,
  COLUMN_LIST: PropTypes.array
};

export default function ColumnMapView({ currentData = {}, id, setId = () => {}, COLUMN_LIST }) {
  const { masterFileData } = useSelector((state) => state.filemanager);
  const dispatch = useDispatch();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState('');
  const [xlsTableData, setXlsTableData] = useState([]);
  const [enableButton, setEnableButton] = useState(true);
  const [fileRequired, setFileRequired] = useState(true);
  const [columnKeys, setColumnKeys] = useState([]);
  const [fileSize, setFileSize] = useState('');
  const { error, createColumnStatus, isLoading, columnNames, smartHeaders, columnMapId, isSaveLoading } = useSelector(
    (state) => state.columnmap
  );
  const { clientList, checkProjectLinked, projectListByClient } = useSelector((state) => state.client);
  const { categoryListByProject, checkCategoryLinked } = useSelector((state) => state.filecategory);

  useEffect(() => {
    if (error || createColumnStatus) {
      enqueueSnackbar((createColumnStatus && 'Column Map Created Successfully') || (error && error), {
        variant: error ? 'error' : 'success'
      });
      dispatch(resetColumnError());
    }
    if (createColumnStatus) {
      formik.resetForm();
      setPreview('');
      setColumnKeys([]);
      setId(null);
      setEnableButton(true);
      setFileRequired(true);
      setXlsTableData([]);
      dispatch(resetCreateColumnStatus());
    }
  }, [error, createColumnStatus]);

  const handleOpenPreview = () => {
    setOpen(true);
  };

  const handleClosePreview = () => {
    setOpen(false);
  };

  const NewUserSchema = Yup.object().shape({
    client: Yup.object().required('Client is required'),
    project: Yup.object().required('Project is required'),
    cover: fileRequired ? Yup.mixed().required('File is required') : ''
  });

  // after extracting the columns
  useEffect(() => {
    if (columnMapId) {
      let tempArray = [];
      tempArray = smartHeaders?.map((col) => ({
        key: '',
        header_name: col?.header_name,
        required: col?.required
      }));
      setColumnKeys(tempArray);
      setEnableButton(false);
    } else {
      setEnableButton(true);
    }
  }, [columnMapId]);

  // while copying list from previous one
  useEffect(() => {
    if (currentData && id) {
      let tempArray = [];
      tempArray = currentData?.mapped_fields?.map((col) => ({
        key: col.key,
        header_name: col.header_name,
        required: col.required
      }));
      dispatch(getCopyExternalColumns(currentData?.mapped_fields));
      setColumnKeys(tempArray);
      // setColumnKeys(currentData?.mapped_fields);
      setEnableButton(false);
      if (currentData?.excel_file) {
        setPreview(currentData?.excel_file);
        getFilePreview(currentData?.excel_file);
      } else if (currentData?.master_file) {
        setPreview(currentData?.master_file.file_name);
        getFilePreview(currentData?.master_file);
        setFileRequired(false);
      }
    } else {
      setEnableButton(true);
    }
  }, [currentData, id]);

  // while copying from previous one converting it into link into file
  const getFilePreview = (file) => {
    let masterFile;
    if (file) {
      axios({
        url: file,
        method: 'GET',
        responseType: 'blob'
      }).then((blob) => {
        masterFile = new File([blob.data], file?.substring(file?.lastIndexOf('/') + 1), {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        setFileSize(fData(masterFile?.size));
        handleXlsFilePreview(masterFile);
      });
    }
  };

  const copyData = {
    copyClient: currentData.client ? currentData.client : '',
    copyProject: currentData.project ? currentData.project : '',
    copyFilemanager: currentData.master_file ? currentData.master_file : null,
    copyFileCategory: currentData.file_type ? currentData.file_type : null,
    copyCover: currentData.excel_file ? currentData.excel_file : null
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      client: id ? copyData.copyClient : null,
      project: id ? copyData.copyProject : null,
      fileCategory: id ? copyData.copyFileCategory : null,
      fileManager: id ? copyData.copyFilemanager : null,
      cover: id ? copyData.copyCover : null,
      maplist: ''
    },
    validationSchema: NewUserSchema,
    onSubmit: async (values, { setSubmitting, setErrors }) => {
      try {
        if (!enableButton) {
          const formData = new FormData();
          formData.append(
            fileRequired ? 'excel_file' : 'master_file',
            fileRequired ? values.cover : values.fileManager.id
          );
          formData.append('client', values.client.id);
          formData.append('project', values.project.id);
          // formData.append('file_type', values.fileCategory.id);
          formData.append('mapped_fields', JSON.stringify(columnKeys));
          dispatch(createColumnmap(formData, !columnMapId ? id : columnMapId));
        } else {
          const formData = new FormData();
          formData.append(fileRequired ? 'file' : 'master_file', fileRequired ? values.cover : values.fileManager.id);
          formData.append('client', values.client.id);
          formData.append('project', values.project.id);
          formData.append('file_type', values.fileCategory.id);
          dispatch(getColumnNames(formData));
        }
        setSubmitting(false);
      } catch (error) {
        console.error(error);
        setErrors(error);
        setSubmitting(false);
      }
    }
  });
  const { errors, values, touched, handleSubmit, setFieldValue, setFieldError } = formik;

  const handleDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        setEnableButton(true);
        handleXlsFilePreview(file);
        setFileSize(fData(file?.size));
        setPreview(file.path);
        setFieldValue('cover', file);
      }
    },
    [setFieldValue]
  );

  const handleXlsFilePreview = async (file) => {
    const data = await file.arrayBuffer();
    const workBook = XLSX.read(data);
    const workSheets = workBook.Sheets[workBook.SheetNames[0]];
    const jsonData = XLSX.utils.sheet_to_json(workSheets, {
      header: 1,
      defval: ''
    });
    setXlsTableData(jsonData);
  };

  const handleMapHeaders = (value, col) => {
    const updateColumns = columnKeys?.map((a) => {
      if (a?.header_name === col?.header_name) {
        a.key = value;
      }
      return a;
    });
    setColumnKeys(updateColumns);
  };

  const handleRequired = (value, col) => {
    const updateColumns = columnKeys?.map((a) => {
      if (a?.header_name === col?.header_name) {
        a.required = value;
      }
      return a;
    });
    setColumnKeys(updateColumns);
  };

  const handleCopyFromList = () => {
    if (!values?.maplist) {
      setFieldError('maplist', 'Select a list from mapping list ');
    } else {
      setId(values?.maplist?.id);
    }
  };

  return (
    <>
      <FormikProvider value={formik}>
        <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={12}>
              <Card sx={{ p: 3 }}>
                <Stack spacing={3}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 3, sm: 2 }} alignItems="center">
                    <Autocomplete
                      name="columnMapList"
                      fullWidth
                      value={values?.maplist ? values?.maplist : null}
                      options={COLUMN_LIST}
                      onChange={(e, value) => setFieldValue('maplist', value)}
                      getOptionLabel={(option) => (option ? option?.columnmap_name : '')}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderInput={(params) => (
                        <TextField
                          error={Boolean(errors?.maplist)}
                          helperText={errors.maplist}
                          {...params}
                          label="Or Copy Mapping From"
                          margin="none"
                        />
                      )}
                    />
                    <Box>
                      <Button variant="contained" type="button" onClick={handleCopyFromList}>
                        Load
                      </Button>
                    </Box>
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 3, sm: 2 }}>
                    <Autocomplete
                      name="client"
                      fullWidth
                      value={values?.client ? values?.client : null}
                      options={clientList}
                      onChange={(e, value) => {
                        setFieldValue('project', '');
                        setFieldValue('fileCategory', '');
                        setFieldValue('client', value);
                        dispatch(resetCategoryListByProject());
                        if (value) {
                          dispatch(getProjectListByClient(value.id));
                        } else {
                          dispatch(resetProjectListByClient());
                        }
                      }}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      getOptionLabel={(option) => (option ? option?.client_name : '')}
                      renderInput={(params) => (
                        <TextField
                          error={Boolean(
                            (touched.client && errors.client) || checkProjectLinked || checkCategoryLinked
                          )}
                          helperText={(touched.client && errors.client) || checkProjectLinked}
                          required
                          {...params}
                          label="For Client"
                          margin="none"
                        />
                      )}
                    />
                    <Autocomplete
                      fullWidth
                      name="project"
                      value={values.project ? values.project : null}
                      options={projectListByClient}
                      onChange={(e, value) => {
                        setFieldValue('fileCategory', '');
                        setFieldValue('project', value);
                        if (value) {
                          dispatch(getCategoryListByProject(value.id));
                        } else {
                          dispatch(resetCategoryListByProject());
                        }
                      }}
                      getOptionLabel={(option) => (option ? option?.project_name : '')}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderInput={(params) => (
                        <TextField
                          error={Boolean((touched.project && errors.project) || checkCategoryLinked)}
                          helperText={(touched.project && errors.project) || checkCategoryLinked}
                          required
                          {...params}
                          label="For Project"
                          margin="none"
                        />
                      )}
                    />
                  </Stack>

                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 3, sm: 2 }}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Autocomplete
                      fullWidth
                      name="fileCategory"
                      value={values.fileCategory ? values.fileCategory : null}
                      onChange={(e, value) => setFieldValue('fileCategory', value)}
                      options={categoryListByProject}
                      getOptionLabel={(option) => (option ? option?.category_name : '')}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderInput={(params) => (
                        <TextField
                          error={Boolean(touched.fileCategory && errors.fileCategory)}
                          helperText={touched.fileCategory && errors.fileCategory}
                          required
                          {...params}
                          label="File Type"
                          margin="none"
                        />
                      )}
                    />
                    <Autocomplete
                      fullWidth
                      value={values?.fileManager ? values?.fileManager : null}
                      name="fileManager"
                      options={masterFileData}
                      onChange={(e, value) => {
                        if (value) {
                          setFileRequired(false);
                        } else {
                          setFileRequired(true);
                        }
                        setFieldValue('fileManager', value);
                        setEnableButton(true);
                        setPreview(value?.file_name);
                        getFilePreview(value?.master_file);
                      }}
                      getOptionLabel={(option) => (option ? option.file_name : '')}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderInput={(params) => <TextField {...params} label="Select From File Manager" margin="none" />}
                    />
                  </Stack>

                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 3, sm: 2 }}
                    justifyContent="space-evenly"
                    alignItems="center"
                  >
                    {!values?.fileManager && (
                      <Box>
                        <div>
                          <LabelStyle>Upload File*</LabelStyle>
                          <UploadSingleFile
                            maxSize={2000000}
                            accept=".xlsx"
                            file={values.cover}
                            onDrop={handleDrop}
                            error={Boolean(touched.cover && errors.cover)}
                          />
                          {touched.cover && errors.cover && (
                            <FormHelperText error sx={{ px: 2 }}>
                              {touched.cover && errors.cover}
                            </FormHelperText>
                          )}
                        </div>
                      </Box>
                    )}
                  </Stack>

                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 3, sm: 2 }}
                    justifyContent="space-evenly"
                    alignItems="center"
                  >
                    {preview && (
                      <List disablePadding>
                        <AnimatePresence>
                          <ListItem
                            component={motion.div}
                            {...varFadeInRight}
                            sx={{
                              my: 1,
                              py: 0.75,
                              px: 2,
                              borderRadius: 1,
                              color: (theme) => `${theme.palette.primary.main}`,
                              border: (theme) => `solid 1px ${theme.palette.primary.main}`,
                              bgcolor: 'background.paper'
                            }}
                          >
                            <ListItemIcon>
                              <Icon icon={fileFill} color="inherit" width={28} height={28} />
                            </ListItemIcon>
                            <ListItemText
                              primary={preview}
                              secondary={fileSize}
                              primaryTypographyProps={{ variant: 'subtitle2' }}
                              secondaryTypographyProps={{ variant: 'caption' }}
                            />
                            {!(currentData && id) && (
                              <MIconButton
                                onClick={() => {
                                  setPreview('');
                                  setFieldValue('cover', null);
                                  setFieldValue('fileManager', null);
                                  setFileRequired(true);
                                  setXlsTableData([]);
                                }}
                                edge="end"
                                size="small"
                              >
                                <Icon icon={closeFill} />
                              </MIconButton>
                            )}
                          </ListItem>
                        </AnimatePresence>
                      </List>
                    )}
                  </Stack>

                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 3, sm: 2 }}
                    sx={{ justifyContent: 'end', alignItems: 'center' }}
                  >
                    <Button variant="outlined" type="button" onClick={handleOpenPreview}>
                      Preview
                    </Button>
                    <LoadingButton disabled={!enableButton} variant="contained" loading={isLoading} type="submitt">
                      Extract Column Names
                    </LoadingButton>
                  </Stack>

                  {!enableButton && (
                    <Stack
                      direction={{ xs: 'row', sm: 'column' }}
                      spacing={{ xs: 2, sm: 3 }}
                      style={{ marginTop: '40px' }}
                    >
                      <Box textAlign="center">
                        <Typography variant="h5" color={theme.palette.primary.main}>
                          {columnNames?.length} Map Colums
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle1">SmartCollect Header</Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '52%' }}>
                          <Typography variant="subtitle1">Import Sheet</Typography>
                          <Typography variant="subtitle1">Required Header</Typography>
                        </Box>
                      </Box>

                      {columnKeys?.map((col, index) => (
                        <Stack key={index} direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 3, sm: 2 }}>
                          <TextField fullWidth label={col?.header_name} disabled />
                          <Autocomplete
                            fullWidth
                            // disabled={col?.key === 'Ignore'}
                            name="Select Columns"
                            options={columnNames}
                            value={col?.key === '' ? null : col?.key}
                            onChange={(e, value) => handleMapHeaders(value, col)}
                            getOptionLabel={(option) => option}
                            getOptionDisabled={(option) => {
                              if (columnKeys.some((a) => a?.key === option)) {
                                return true;
                              }
                              if (columnKeys.some((a) => a?.key === 'Ignore')) {
                                return true;
                              }
                              return false;
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                required={col?.required}
                                label="Select Columns"
                                margin="none"
                                //
                              />
                            )}
                          />
                          <FormControlLabel
                            control={
                              col?.required ? (
                                <Checkbox defaultChecked={col?.required} />
                              ) : (
                                <Checkbox
                                  disabled={col?.key === 'Ignore'}
                                  checked={col?.required}
                                  onChange={(e, value) => handleRequired(value, col)}
                                />
                              )
                            }
                            label=""
                          />
                        </Stack>
                      ))}

                      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                        <LoadingButton type="submit" variant="contained" loading={isSaveLoading}>
                          Save
                        </LoadingButton>
                      </Box>
                    </Stack>
                  )}
                </Stack>
              </Card>
            </Grid>
          </Grid>
        </Form>
      </FormikProvider>

      <FilePreview
        formik={formik}
        openPreview={open}
        onClosePreview={handleClosePreview}
        CalledComponent={previewColumnFile}
        showableContent={xlsTableData}
      />
    </>
  );
}
